import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, formatApiErrorDetail } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { UserPlus, Buildings, ShieldCheck, Warning, Check, Plus, Trash, Bank, Key, Wrench, ClipboardText, FileText, X, MagnifyingGlass, Sparkle } from "@phosphor-icons/react";

const SERVICES = [
  { key: "maintenance", label: "Maintenance Only (24/7 work-order dispatch)" },
  { key: "full_pm", label: "Full Property Management (rent + tenants + everything)" },
  { key: "leasing", label: "Leasing & Placement (tenant screening, showings)" },
  { key: "turns_renovations", label: "Turnovers (paint, clean, punch)" },
  { key: "renovations", label: "Renovation Project Management" },
  { key: "snow_lawn", label: "Snow & Lawn / Seasonal Services" },
  { key: "vacant_security", label: "Vacant Property Security (cameras + alarms)" },
  { key: "inspections", label: "Move-In / Move-Out Inspections + PDF reports" },
  { key: "eviction_support", label: "Eviction Support (paperwork + court prep)" },
  { key: "compliance_only", label: "Philly L&I Compliance Only" },
];

export default function LandlordOnboardPublic() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  const [contact, setContact] = useState({
    full_name: "", company_llc: "", email: "", phone: "",
    mailing_address: "", principal_name: "",
  });
  const [properties, setProperties] = useState([blankProperty()]);
  const [banking, setBanking] = useState({
    bank_name: "", account_holder: "", routing_last4: "",
    account_last4: "", account_type: "checking",
  });
  const [access, setAccess] = useState({
    lockbox_code: "", alarm_code: "", key_location: "", access_notes: "",
  });
  const [preferences, setPreferences] = useState({
    vendor_prefs: "", spending_cap_cents: 25000,
    emergency_contact_name: "", emergency_contact_phone: "",
    referral_source: "", special_instructions: "",
  });
  const [services, setServices] = useState([]);
  const [acks, setAcks] = useState({
    lead_paint_disclosure_ack: false, fair_housing_ack: false,
    pa_landlord_tenant_act_ack: false, good_cause_awareness_ack: false,
    security_deposit_two_month_cap_ack: false, radon_awareness_ack: false,
  });
  const [signedName, setSignedName] = useState("");
  const [uploaded, setUploaded] = useState([]);  // {path, kind, size}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/landlord-onboarding/${token}`)
      .then(r => {
        setInvite(r.data);
        if (r.data.existing_submission) setDone(true);
        if (r.data.invited_name) setContact(c => ({...c, full_name: r.data.invited_name}));
        if (r.data.invited_email) setContact(c => ({...c, email: r.data.invited_email}));
        if (r.data.invited_phone) setContact(c => ({...c, phone: r.data.invited_phone}));
        if (r.data.referral_source) setPreferences(p => ({...p, referral_source: r.data.referral_source}));
      })
      .catch(e => setErr(e.response?.status === 410 ? "This invite has expired or was revoked."
                                                    : "Invite not found."));
  }, [token]);

  function blankProperty() {
    return {
      address: "", unit: "", beds: 0, baths: 0, sqft: 0,
      current_tenant_name: "", current_tenant_email: "",
      current_rent_cents: 0, lease_end: "", year_built: 0,
      philly_rental_license_status: "unknown",
      philly_rental_license_number: "",
      lead_cert_current: false, notes: "",
    };
  }

  const addProperty = () => setProperties([...properties, blankProperty()]);
  const patchProperty = (i, k, v) => {
    const next = [...properties]; next[i] = {...next[i], [k]: v};
    setProperties(next);
  };
  const delProperty = (i) => {
    if (properties.length === 1) return toast.error("At least one property required");
    const next = [...properties]; next.splice(i, 1); setProperties(next);
  };
  const toggleService = (key) => {
    setServices(s => s.includes(key) ? s.filter(x => x !== key) : [...s, key]);
  };

  const uploadDoc = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(
        `${API}/public/landlord-onboarding/${token}/document?kind=${encodeURIComponent(kind)}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } }
      );
      setUploaded(u => [...u, r.data]);
      toast.success(`${kind} uploaded`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Upload failed")); }
  };

  const [aiBusy, setAiBusy] = useState(false);
  const [aiReport, setAiReport] = useState(null);

  const runAiExtract = async () => {
    if (uploaded.length === 0) return toast.error("Upload at least one document first (lease, W-9, COI, or ID)");
    setAiBusy(true); setAiReport(null);
    try {
      const r = await axios.post(`${API}/public/landlord-onboarding/${token}/ai-extract`);
      const d = r.data || {};
      const filled = [];
      // Contact — only fill empty fields
      setContact(c => {
        const nc = { ...c };
        for (const k of ["full_name", "company_llc", "email", "phone", "mailing_address"]) {
          if (!nc[k] && d.contact?.[k]) { nc[k] = d.contact[k]; filled.push(`contact.${k}`); }
        }
        return nc;
      });
      // Banking
      setBanking(b => {
        const nb = { ...b };
        for (const k of ["bank_name", "account_holder", "routing_last4", "account_last4", "account_type"]) {
          if ((!nb[k] || nb[k] === "checking") && d.banking?.[k]) { nb[k] = d.banking[k]; filled.push(`banking.${k}`); }
        }
        return nb;
      });
      // Properties — if user has only the blank template, replace entirely
      if (Array.isArray(d.properties) && d.properties.length > 0) {
        setProperties(prev => {
          const isBlankOnly = prev.length === 1 && !prev[0].address?.trim();
          const mapped = d.properties.map(p => ({
            ...blankProperty(),
            address: p.address || "",
            unit: p.unit || "",
            current_rent_cents: (p.current_rent_cents || 0) / 100,
            lease_end: p.lease_end || "",
            current_tenant_name: p.current_tenant_name || "",
            current_tenant_email: p.current_tenant_email || "",
            year_built: p.year_built || 0,
          }));
          filled.push(`${mapped.length} propert${mapped.length === 1 ? "y" : "ies"}`);
          return isBlankOnly ? mapped : [...prev, ...mapped];
        });
      }
      setAiReport({
        confidence: d.confidence,
        notes: d.notes,
        docs_read: d.docs_read,
        filled,
        insurance: d.insurance,
      });
      toast.success(`AI filled ${filled.length} field${filled.length === 1 ? "" : "s"} from ${d.docs_read} doc${d.docs_read === 1 ? "" : "s"}`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "AI extract failed")); }
    finally { setAiBusy(false); }
  };

  const hasPre1978 = properties.some(p => Number(p.year_built) > 0 && Number(p.year_built) < 1978);

  const submit = async () => {
    if (!contact.full_name && !contact.company_llc) return toast.error("Please provide your name or company");
    if (!properties[0]?.address) return toast.error("Please add at least one property with an address");
    if (!signedName.trim()) return toast.error("Please type your name to sign");
    if (hasPre1978 && !acks.lead_paint_disclosure_ack) return toast.error("Lead paint disclosure required for pre-1978 properties");

    setBusy(true);
    try {
      const props = properties.map(p => ({
        ...p,
        beds: parseInt(p.beds) || 0,
        baths: parseFloat(p.baths) || 0,
        sqft: parseInt(p.sqft) || 0,
        year_built: parseInt(p.year_built) || 0,
        current_rent_cents: Math.round((parseFloat(p.current_rent_cents) || 0) * 100),
      }));
      await axios.post(`${API}/public/landlord-onboarding/${token}/submit`, {
        contact, properties: props, banking, access,
        preferences: {
          ...preferences,
          spending_cap_cents: Math.round((parseFloat(preferences.spending_cap_cents) || 0) * 100),
        },
        services, ...acks, signed_by_name: signedName.trim(),
      });
      setDone(true);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Submit failed")); }
    finally { setBusy(false); }
  };

  if (err) return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
      <div className="max-w-md bg-white p-6 border-4 border-slate-900 shadow-[6px_6px_0_#0f172a] text-center">
        <Warning size={48} className="text-red-500 mx-auto mb-3"/>
        <div className="font-black uppercase tracking-widest text-lg">{err}</div>
      </div>
    </div>
  );
  if (!invite) return <div className="min-h-screen flex items-center justify-center font-black">Loading…</div>;

  const brand = invite.tenant_brand_color || "#0f172a";

  if (done) return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
      <div className="max-w-md bg-white p-8 border-4 border-emerald-600 shadow-[6px_6px_0_#0f172a] text-center" data-testid="submitted-panel">
        {invite.tenant_has_logo && invite.tenant_id && (
          <img src={`${API}/tenant/logo/${invite.tenant_id}`} alt={invite.tenant_brand_name}
            className="max-h-12 mx-auto mb-3"
            onError={e => { e.currentTarget.style.display = "none"; }}/>
        )}
        <Check weight="bold" size={56} className="text-emerald-600 mx-auto mb-3"/>
        <div className="font-black uppercase tracking-widest text-xl">Thank you</div>
        <div className="text-sm text-slate-600 mt-2">
          Your onboarding intake has been submitted to <b>{invite.tenant_brand_name}</b>.
          The office will review and reach out within 2 business days.
        </div>
        {(invite.tenant_contact_email || invite.tenant_contact_phone) && (
          <div className="text-xs text-slate-500 mt-4">
            Questions in the meantime?
            {invite.tenant_contact_email && <> <a href={`mailto:${invite.tenant_contact_email}`} className="text-emerald-700 hover:underline">{invite.tenant_contact_email}</a></>}
            {invite.tenant_contact_phone && <> · <a href={`tel:${invite.tenant_contact_phone}`} className="text-emerald-700 hover:underline">{invite.tenant_contact_phone}</a></>}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100" data-testid="onboard-public">
      <Toaster position="top-right" richColors closeButton/>
      <div className="max-w-3xl mx-auto p-4 md:p-8 space-y-4">
        <div className="p-6 text-white shadow-[6px_6px_0_#0f172a]" style={{ background: brand }}>
          {invite.tenant_has_logo && invite.tenant_id && (
            <img src={`${API}/tenant/logo/${invite.tenant_id}`} alt={invite.tenant_brand_name}
              className="max-h-14 max-w-[220px] mb-3 bg-white/10 p-1"
              onError={e => { e.currentTarget.style.display = "none"; }}/>
          )}
          <div className="text-[10px] font-black uppercase tracking-widest opacity-70">{invite.tenant_brand_name}</div>
          <div className="flex items-center gap-2 mt-1">
            <UserPlus weight="fill" size={28}/>
            <h1 className="text-2xl md:text-3xl font-black uppercase tracking-widest">Welcome{contact.full_name ? `, ${contact.full_name.split(" ")[0]}` : ""}!</h1>
          </div>
          <div className="text-sm mt-2 opacity-90">
            <b>{invite.tenant_brand_name}</b> is excited to work with you. This intake takes about 10 minutes — we'll capture your properties, service preferences, banking, and Pennsylvania & Philadelphia compliance details, then get your portfolio live in our system.
          </div>
          {(invite.tenant_contact_email || invite.tenant_contact_phone) && (
            <div className="text-[10px] mt-3 opacity-80 flex flex-wrap gap-3">
              {invite.tenant_contact_email && (
                <a href={`mailto:${invite.tenant_contact_email}`} className="underline">
                  Questions? {invite.tenant_contact_email}
                </a>
              )}
              {invite.tenant_contact_phone && (
                <a href={`tel:${invite.tenant_contact_phone}`} className="underline">
                  {invite.tenant_contact_phone}
                </a>
              )}
            </div>
          )}
        </div>

        <PoweredByStrip tenantName={invite.tenant_brand_name}/>

        {/* Contact */}
        <SectionCard title="Your Info" icon={<UserPlus/>}>
          <input value={contact.full_name} onChange={e => setContact({...contact, full_name: e.target.value})}
            placeholder="Full name *" data-testid="onb-name"
            className="w-full h-11 px-3 border-2 border-slate-300 text-sm"/>
          <input value={contact.company_llc} onChange={e => setContact({...contact, company_llc: e.target.value})}
            placeholder="Company / LLC (if any)"
            className="w-full h-11 px-3 border-2 border-slate-300 text-sm"/>
          <input value={contact.principal_name} onChange={e => setContact({...contact, principal_name: e.target.value})}
            placeholder="Principal / portfolio grouping name (if you own multiple LLCs — e.g. 'Jane Smith')"
            className="w-full h-11 px-3 border-2 border-slate-300 text-sm"/>
          <div className="grid grid-cols-2 gap-2">
            <input value={contact.email} onChange={e => setContact({...contact, email: e.target.value})}
              placeholder="Email *" data-testid="onb-email"
              className="h-11 px-3 border-2 border-slate-300 text-sm"/>
            <input value={contact.phone} onChange={e => setContact({...contact, phone: e.target.value})}
              placeholder="Phone"
              className="h-11 px-3 border-2 border-slate-300 text-sm"/>
          </div>
          <input value={contact.mailing_address} onChange={e => setContact({...contact, mailing_address: e.target.value})}
            placeholder="Mailing address"
            className="w-full h-11 px-3 border-2 border-slate-300 text-sm"/>
        </SectionCard>

        {/* Properties */}
        <SectionCard title={`Properties (${properties.length})`} icon={<Buildings/>}>
          {properties.map((p, i) => (
            <div key={i} className="p-3 border-2 border-slate-300 space-y-2" data-testid={`onb-prop-${i}`}>
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Property #{i+1}</div>
                {properties.length > 1 && (
                  <button onClick={() => delProperty(i)} data-testid={`onb-prop-del-${i}`}
                    className="h-7 w-7 border-2 border-red-600 text-red-600 hover:bg-red-50 inline-flex items-center justify-center">
                    <Trash size={12}/>
                  </button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <input value={p.address} onChange={e => patchProperty(i, "address", e.target.value)}
                  placeholder="Full address * (e.g. 5346 Chew Ave)" data-testid={`onb-prop-addr-${i}`}
                  className="col-span-2 h-10 px-3 border-2 border-slate-300 text-sm"/>
                <input value={p.unit} onChange={e => patchProperty(i, "unit", e.target.value)} placeholder="Unit"
                  className="h-10 px-3 border-2 border-slate-300 text-sm"/>
              </div>
              <LookupButton token={token} address={p.address} onFill={(data) => {
                const upd = { ...p };
                if (data.year_built) upd.year_built = data.year_built;
                if (data.rental_license_status) upd.philly_rental_license_status = data.rental_license_status;
                if (data.rental_license_number) upd.philly_rental_license_number = data.rental_license_number;
                if (data.lead_cert_required !== undefined) {
                  // Only auto-check if landlord's cert is already true; else leave alone
                }
                const next = [...properties]; next[i] = upd; setProperties(next);
              }}/>
              <div className="grid grid-cols-4 gap-2">
                <input type="number" value={p.beds} onChange={e => patchProperty(i, "beds", e.target.value)} placeholder="Beds"
                  className="h-10 px-2 border-2 border-slate-300 text-sm"/>
                <input type="number" step="0.5" value={p.baths} onChange={e => patchProperty(i, "baths", e.target.value)} placeholder="Baths"
                  className="h-10 px-2 border-2 border-slate-300 text-sm"/>
                <input type="number" value={p.sqft} onChange={e => patchProperty(i, "sqft", e.target.value)} placeholder="Sqft"
                  className="h-10 px-2 border-2 border-slate-300 text-sm"/>
                <input type="number" value={p.year_built} onChange={e => patchProperty(i, "year_built", e.target.value)} placeholder="Yr built"
                  className="h-10 px-2 border-2 border-slate-300 text-sm"/>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input value={p.current_tenant_name} onChange={e => patchProperty(i, "current_tenant_name", e.target.value)}
                  placeholder="Current tenant (blank = vacant)"
                  className="h-10 px-3 border-2 border-slate-300 text-sm"/>
                <input value={p.current_tenant_email} onChange={e => patchProperty(i, "current_tenant_email", e.target.value)}
                  placeholder="Tenant email"
                  className="h-10 px-3 border-2 border-slate-300 text-sm"/>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" value={p.current_rent_cents} onChange={e => patchProperty(i, "current_rent_cents", e.target.value)}
                  placeholder="Current rent ($)"
                  className="h-10 px-3 border-2 border-slate-300 text-sm"/>
                <input value={p.lease_end} onChange={e => patchProperty(i, "lease_end", e.target.value)}
                  placeholder="Lease end (YYYY-MM-DD)"
                  className="h-10 px-3 border-2 border-slate-300 text-sm"/>
              </div>
              <div className="p-2 bg-amber-50 border-l-4 border-amber-500 space-y-2">
                <div className="text-[10px] font-black uppercase tracking-widest text-amber-700">Philly L&I</div>
                <div className="grid grid-cols-2 gap-2">
                  <select value={p.philly_rental_license_status} onChange={e => patchProperty(i, "philly_rental_license_status", e.target.value)}
                    className="h-10 px-2 border-2 border-slate-300 text-xs">
                    <option value="unknown">License — Unknown</option>
                    <option value="current">Current</option>
                    <option value="expired">Expired</option>
                    <option value="none">None</option>
                  </select>
                  <input value={p.philly_rental_license_number} onChange={e => patchProperty(i, "philly_rental_license_number", e.target.value)}
                    placeholder="License # (if you have it)"
                    className="h-10 px-2 border-2 border-slate-300 text-xs"/>
                </div>
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={p.lead_cert_current}
                    onChange={e => patchProperty(i, "lead_cert_current", e.target.checked)}/>
                  Lead-safe certification is current (required for pre-1978 rentals with kids under 6)
                </label>
              </div>
              <textarea value={p.notes} onChange={e => patchProperty(i, "notes", e.target.value)} rows={2}
                placeholder="Anything we should know about this property?"
                className="w-full p-2 border-2 border-slate-300 text-sm"/>
            </div>
          ))}
          <button onClick={addProperty} data-testid="onb-add-prop"
            className="w-full h-11 border-2 border-dashed border-slate-400 hover:bg-slate-50 font-black uppercase tracking-widest text-[11px] inline-flex items-center justify-center gap-1">
            <Plus size={14}/> Add Another Property
          </button>
        </SectionCard>

        {/* Services */}
        <SectionCard title="What services do you need?" icon={<Wrench/>}>
          <div className="grid grid-cols-1 gap-1">
            {SERVICES.map(sv => (
              <label key={sv.key} className="flex items-start gap-2 p-2 hover:bg-slate-50 cursor-pointer border border-slate-200"
                data-testid={`onb-service-${sv.key}`}>
                <input type="checkbox" checked={services.includes(sv.key)} onChange={() => toggleService(sv.key)}
                  className="mt-0.5"/>
                <span className="text-sm">{sv.label}</span>
              </label>
            ))}
          </div>
        </SectionCard>

        {/* Documents */}
        <SectionCard title="Documents" icon={<FileText/>}>
          <div className="text-xs text-slate-600 mb-2">
            Upload each doc as a PDF or clear photo. Once at least one is uploaded, hit <b>AI Auto-Fill</b> below and we'll hydrate the whole form from your papers — no re-typing.
          </div>
          {[
            { kind: "w9", label: "W-9 (required for 1099 reporting)" },
            { kind: "coi", label: "Certificate of Insurance (landlord dwelling policy)" },
            { kind: "id", label: "Driver's license or government ID" },
            { kind: "existing_lease", label: "Current lease agreement (per unit)" },
            { kind: "deed", label: "Deed or proof of ownership" },
            { kind: "voided_check", label: "Voided check or bank letter (for ACH last-4)" },
          ].map(d => (
            <div key={d.kind} className="flex items-center gap-2 p-2 border border-slate-200">
              <div className="min-w-0 flex-1 text-sm">{d.label}</div>
              <label className="h-9 px-3 border-2 border-slate-900 hover:bg-slate-50 font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1 cursor-pointer"
                data-testid={`onb-doc-${d.kind}`}>
                <Plus size={12}/> Upload
                <input type="file" accept=".pdf,image/*" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) uploadDoc(d.kind, f); e.target.value = ""; }}/>
              </label>
              {uploaded.some(u => u.kind?.includes(d.kind)) && <Check size={16} className="text-emerald-600 shrink-0"/>}
            </div>
          ))}

          <div className="p-3 bg-fuchsia-50 border-2 border-fuchsia-500 mt-2 space-y-2" data-testid="onb-ai-extract-panel">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[11px] font-black uppercase tracking-widest text-fuchsia-800 flex items-center gap-1">
                <Sparkle weight="fill" size={12}/> Onboarding-in-a-Day
              </div>
              <button onClick={runAiExtract} disabled={aiBusy || uploaded.length === 0}
                data-testid="onb-ai-extract-btn"
                className="h-10 px-4 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-[11px] shadow-[3px_3px_0_#0f172a] inline-flex items-center gap-1 disabled:opacity-40">
                <Sparkle weight="fill" size={12}/> {aiBusy ? "Reading your docs…" : "AI Auto-Fill from Docs"}
              </button>
            </div>
            <div className="text-[11px] text-slate-700">
              {uploaded.length === 0
                ? "Upload at least one doc above to enable AI auto-fill."
                : `${uploaded.length} doc${uploaded.length === 1 ? "" : "s"} ready to read.`}
            </div>
            {aiReport && (
              <div className="p-2 bg-white border-2 border-fuchsia-300 text-[11px] space-y-1" data-testid="onb-ai-report">
                <div className="font-black uppercase tracking-widest text-[9px] text-fuchsia-700">
                  Confidence: {aiReport.confidence} · Read {aiReport.docs_read} doc{aiReport.docs_read === 1 ? "" : "s"}
                </div>
                {aiReport.notes && <div className="text-slate-700">{aiReport.notes}</div>}
                {aiReport.filled?.length > 0 && (
                  <div className="text-emerald-700">✓ Filled: {aiReport.filled.slice(0, 6).join(", ")}{aiReport.filled.length > 6 ? ` +${aiReport.filled.length - 6} more` : ""}</div>
                )}
                {aiReport.insurance?.carrier && (
                  <div className="text-slate-700">
                    Insurance detected: <b>{aiReport.insurance.carrier}</b>
                    {aiReport.insurance.policy_number && ` · #${aiReport.insurance.policy_number}`}
                    {aiReport.insurance.expiration_date && ` · exp ${aiReport.insurance.expiration_date}`}
                    <div className="text-[9px] italic text-slate-500">Office will confirm and enter into the insurance vault after review.</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </SectionCard>

        {/* Banking */}
        <SectionCard title="Banking (for rent payouts / vendor reimbursement)" icon={<Bank/>}>
          <div className="text-xs text-slate-600 mb-1">
            Only the last 4 digits are stored here. Office will collect full ACH details securely offline.
          </div>
          <input value={banking.bank_name} onChange={e => setBanking({...banking, bank_name: e.target.value})}
            placeholder="Bank name"
            className="w-full h-11 px-3 border-2 border-slate-300 text-sm"/>
          <input value={banking.account_holder} onChange={e => setBanking({...banking, account_holder: e.target.value})}
            placeholder="Account holder name"
            className="w-full h-11 px-3 border-2 border-slate-300 text-sm"/>
          <div className="grid grid-cols-3 gap-2">
            <input maxLength={4} value={banking.routing_last4} onChange={e => setBanking({...banking, routing_last4: e.target.value.replace(/\D/g, "").slice(0, 4)})}
              placeholder="Routing last 4"
              className="h-11 px-3 border-2 border-slate-300 text-sm"/>
            <input maxLength={4} value={banking.account_last4} onChange={e => setBanking({...banking, account_last4: e.target.value.replace(/\D/g, "").slice(0, 4)})}
              placeholder="Account last 4"
              className="h-11 px-3 border-2 border-slate-300 text-sm"/>
            <select value={banking.account_type} onChange={e => setBanking({...banking, account_type: e.target.value})}
              className="h-11 px-3 border-2 border-slate-300 text-sm">
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
            </select>
          </div>
        </SectionCard>

        {/* Access */}
        <SectionCard title="Keys & Access" icon={<Key/>}>
          <div className="grid grid-cols-2 gap-2">
            <input value={access.lockbox_code} onChange={e => setAccess({...access, lockbox_code: e.target.value})}
              placeholder="Lockbox code (if any)"
              className="h-11 px-3 border-2 border-slate-300 text-sm"/>
            <input value={access.alarm_code} onChange={e => setAccess({...access, alarm_code: e.target.value})}
              placeholder="Alarm code (if any)"
              className="h-11 px-3 border-2 border-slate-300 text-sm"/>
          </div>
          <input value={access.key_location} onChange={e => setAccess({...access, key_location: e.target.value})}
            placeholder="Where are the keys today?"
            className="w-full h-11 px-3 border-2 border-slate-300 text-sm"/>
          <textarea value={access.access_notes} onChange={e => setAccess({...access, access_notes: e.target.value})} rows={2}
            placeholder="Any access notes (parking, gate codes, dogs, tenant hours…)"
            className="w-full p-2 border-2 border-slate-300 text-sm"/>
        </SectionCard>

        {/* Preferences */}
        <SectionCard title="Preferences & Special Instructions" icon={<ClipboardText/>}>
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-600 flex flex-col gap-1">
            Spending cap without approval ($)
            <input type="number" value={preferences.spending_cap_cents} onChange={e => setPreferences({...preferences, spending_cap_cents: e.target.value})}
              className="h-11 px-3 border-2 border-slate-300 text-sm"/>
          </label>
          <textarea value={preferences.vendor_prefs} onChange={e => setPreferences({...preferences, vendor_prefs: e.target.value})} rows={2}
            placeholder="Vendor preferences (e.g., 'my nephew handles plumbing at 4241 Chestnut')"
            className="w-full p-2 border-2 border-slate-300 text-sm"/>
          <div className="grid grid-cols-2 gap-2">
            <input value={preferences.emergency_contact_name} onChange={e => setPreferences({...preferences, emergency_contact_name: e.target.value})}
              placeholder="Emergency contact name"
              className="h-11 px-3 border-2 border-slate-300 text-sm"/>
            <input value={preferences.emergency_contact_phone} onChange={e => setPreferences({...preferences, emergency_contact_phone: e.target.value})}
              placeholder="Emergency contact phone"
              className="h-11 px-3 border-2 border-slate-300 text-sm"/>
          </div>
          <textarea value={preferences.special_instructions} onChange={e => setPreferences({...preferences, special_instructions: e.target.value})} rows={3}
            placeholder="Anything else? (approval workflows, budget rules, communication preferences…)"
            className="w-full p-2 border-2 border-slate-300 text-sm"/>
        </SectionCard>

        {/* Acks */}
        <SectionCard title="PA / Philly Acknowledgments" icon={<ShieldCheck/>}>
          <div className="text-xs text-slate-600 mb-2">
            Please review and check each item that applies. Some may be required based on your properties.
          </div>
          {[
            { key: "lead_paint_disclosure_ack",
              label: "I understand federal + PA lead paint disclosure is required for pre-1978 rentals.",
              req: hasPre1978 },
            { key: "fair_housing_ack",
              label: "I will comply with federal + PA Fair Housing (no discrimination on race, color, religion, sex, national origin, disability, familial status, age, ancestry)." },
            { key: "pa_landlord_tenant_act_ack",
              label: "I have reviewed / understand PA Landlord & Tenant Act of 1951 (68 P.S. §§ 250.101 et seq.)." },
            { key: "good_cause_awareness_ack",
              label: "I am aware Philadelphia's Good Cause Eviction ordinance may apply to my properties." },
            { key: "security_deposit_two_month_cap_ack",
              label: "I understand PA caps security deposits at 2 months' rent during the first year of a lease, 1 month thereafter." },
            { key: "radon_awareness_ack",
              label: "I am aware PA recommends radon testing for rental properties and will disclose if levels are elevated." },
          ].map(a => (
            <label key={a.key} className={`flex items-start gap-2 p-2 hover:bg-slate-50 cursor-pointer border ${
              a.req && !acks[a.key] ? "border-red-500 bg-red-50" : "border-slate-200"
            }`} data-testid={`onb-ack-${a.key}`}>
              <input type="checkbox" checked={acks[a.key]} onChange={e => setAcks({...acks, [a.key]: e.target.checked})}
                className="mt-0.5"/>
              <span className="text-xs">{a.label}
                {a.req && <span className="ml-1 text-[9px] font-black uppercase tracking-widest text-red-700"> Required</span>}
              </span>
            </label>
          ))}
        </SectionCard>

        {/* Signature + Submit */}
        <SectionCard title="Sign & Submit" icon={<Check/>}>
          <div className="text-xs text-slate-700">
            By typing your full name below, you confirm the information above is accurate to the best of your knowledge.
          </div>
          <input value={signedName} onChange={e => setSignedName(e.target.value)}
            placeholder="Type your full name"
            data-testid="onb-signed-name"
            className="w-full h-12 px-4 border-2 border-slate-900 text-lg font-serif italic"/>
          <button onClick={submit} disabled={busy}
            data-testid="onb-submit"
            className="w-full h-14 bg-emerald-600 text-white font-black uppercase tracking-widest text-lg disabled:opacity-50 shadow-[4px_4px_0_#0f172a]">
            {busy ? "Submitting…" : "Submit Onboarding"}
          </button>
        </SectionCard>
      </div>
    </div>
  );
}

function SectionCard({ title, icon, children }) {
  return (
    <div className="bg-white p-4 border-4 border-slate-900 shadow-[3px_3px_0_#0f172a] space-y-2">
      <h2 className="font-black uppercase tracking-widest text-sm flex items-center gap-2 border-b-2 border-slate-900 pb-2">
        <span className="text-emerald-600">{icon}</span> {title}
      </h2>
      {children}
    </div>
  );
}

export function PoweredByStrip({ tenantName }) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 bg-slate-900 text-white/90 border-4 border-slate-900 shadow-[3px_3px_0_#0f172a] text-[10px] font-black uppercase tracking-widest flex-wrap"
      data-testid="powered-by-strip">
      <div>{tenantName || "Your Property Manager"}</div>
      <div className="opacity-70">
        Powered by <span className="text-cyan-300">Field Radar PRO</span> — property management, done right
      </div>
    </div>
  );
}

function LookupButton({ token, address, onFill }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const lookup = async () => {
    if (!address || address.trim().length < 3) {
      toast.error("Type the property address first");
      return;
    }
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await axios.get(
        `${API}/public/landlord-onboarding/${token}/lookup-address`,
        { params: { address }, timeout: 15000 }
      );
      if (!r.data.found) {
        setErr(r.data.reason || "No match found");
      } else {
        setResult(r.data);
        onFill(r.data);
        toast.success("Auto-filled from Philly's public records");
      }
    } catch (e) { setErr(e.response?.data?.detail || "Lookup failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-2 bg-cyan-50 border-2 border-cyan-500 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-black uppercase tracking-widest text-cyan-800 flex items-center gap-1">
          <Sparkle weight="fill" size={12}/> AI Property Assist
        </div>
        <button onClick={lookup} disabled={busy || !address}
          data-testid={`onb-lookup-${address.slice(0, 8)}`}
          className="h-8 px-3 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase tracking-widest text-[10px] disabled:opacity-50 inline-flex items-center gap-1">
          <MagnifyingGlass size={12}/> {busy ? "Looking up…" : "Auto-Fill from L&I"}
        </button>
      </div>
      {err && (
        <div className="text-[11px] text-slate-700">
          {err}
        </div>
      )}
      {result && (
        <div className="text-[11px] text-slate-700 space-y-0.5" data-testid="lookup-result">
          <div>✓ Matched <b>{result.canonical_address}</b> · Parcel {result.parcel_number}</div>
          {result.owner_of_record && <div>Owner of record: {result.owner_of_record}</div>}
          {result.year_built && <div>Built {result.year_built}{result.lead_cert_required && " · lead cert required"}</div>}
          <div>
            License status: <b>{result.rental_license_status}</b>
            {result.rental_license_number && ` · #${result.rental_license_number}`}
            {result.rental_license_expires && ` · expires ${result.rental_license_expires}`}
          </div>
          <a href={result.atlas_url} target="_blank" rel="noopener noreferrer"
            className="text-cyan-700 hover:underline text-[10px]">
            Open on atlas.phila.gov →
          </a>
        </div>
      )}
    </div>
  );
}
